/**
 * LocalStoreContext – עבודה לוקאלית בלבד ממגוון הסניף.
 * נתונים נשמרים ב-IndexedDB (מגבלת אחסון גבוהה, עובד ב-Safari/iOS) לפי storeId.
 * אחרי טעינת המגוון – אם עברה שעה מאז "התחל הזמנה", מעדכן מגוון ברקע (ללא תמונות).
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/auth/AuthContext";
import { useAreaManagerSession } from "@/contexts/AreaManagerSessionContext";
import { loadPrefetched, savePrefetched, LAST_RECEIPT_QTY_VERSION } from "@/lib/prefetchStorage";
import { runLightPrefetch, fetchStoreSalesByItemCode, fetchHomeSnapshot } from "@/lib/prefetchOrder";
import { shouldPreferLocalData } from "@/lib/connectionQuality";
import { attachSalesStatsToProducts, SALES_STATS_PERIOD_VERSION } from "@/utils/productSalesStats";
import { base44 } from "@/api/base44Client";

const LocalStoreContext = createContext(null);

const HOUR_MS = 60 * 60 * 1000;

/** מגוון ישן מעל 30 דק׳ — רענון ברקע. קצר מדי = חונק ניווט בסניפים עם קליטה גרועה */
const DEFAULT_STALE_MS = 30 * 60_000;
/** לא יותר מרענון קל אחד ל־5 דקות בגלישה רגילה */
const DEFAULT_MIN_INTERVAL_MS = 5 * 60_000;

/** מחזיר "דליית שעה" – אותו מספר לכל הדקות באותה שעה (UTC או לוקאל; כאן לוקאל) */
function getHourBucket(ms) {
  return Math.floor(ms / HOUR_MS);
}

export function LocalStoreProvider({ children }) {
  const { user } = useAuth();
  const { actingStoreId, isActingAsStore, isAreaManager } = useAreaManagerSession();
  const storeId =
    isActingAsStore && actingStoreId
      ? actingStoreId
      : isAreaManager
        ? null
      : user?.store_id?._id ?? user?.store_id ?? null;

  const [localData, setLocalData] = useState(null);
  const [isPrefetchLoading, setIsPrefetchLoading] = useState(!!storeId);
  const [isSyncing, setIsSyncing] = useState(false);
  const lastRefreshedHourBucketRef = useRef(null);
  const catalogTimestampRef = useRef(0);
  const catalogSyncLockRef = useRef({ inFlight: null, lastAttempt: 0 });
  const lastPurchaseBackfillRef = useRef(false);
  const salesStatsBackfillRef = useRef(false);
  const homeSnapshotBackfillRef = useRef(false);
  const autoCatalogPrefetchRef = useRef(false);

  useEffect(() => {
    catalogTimestampRef.current = localData?.timestamp ?? 0;
  }, [localData?.timestamp]);

  useEffect(() => {
    autoCatalogPrefetchRef.current = false;
  }, [storeId]);

  useEffect(() => {
    let cancelled = false;
    let finished = false;
    const fallbackTimer = setTimeout(() => {
      if (finished || cancelled) return;
      setLocalData(null);
      setIsPrefetchLoading(false);
    }, 4000);

    if (!storeId) {
      setLocalData(null);
      setIsPrefetchLoading(false);
      clearTimeout(fallbackTimer);
      return;
    }
    setLocalData(null);
    lastPurchaseBackfillRef.current = false;
    salesStatsBackfillRef.current = false;
    homeSnapshotBackfillRef.current = false;
    setIsPrefetchLoading(true);
    loadPrefetched(storeId)
      .then((data) => {
        if (cancelled) return;
        setLocalData(data);
      })
      .catch(() => {
        if (cancelled) return;
        setLocalData(null);
      })
      .finally(() => {
        finished = true;
        clearTimeout(fallbackTimer);
        if (!cancelled) setIsPrefetchLoading(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
    };
  }, [storeId]);

  const refreshLocal = useCallback(() => {
    if (!storeId) return Promise.resolve();
    return loadPrefetched(storeId).then((data) => {
      setLocalData(data);
      return data;
    });
  }, [storeId]);

  /**
   * מוריד מגוון מעודכן מהשרת אם המטמון המקומי ישן (למשל אחרי שסופר־אדמין שינה סדר מוצרים).
   * מנהלי סניפים עובדים מ־IndexedDB — בלי קריאה כזו הם ימשיכו לראות סדר ישן עד שעה.
   *
   * @param {{ maxAgeMs?: number, minIntervalMs?: number }} [options]
   */
  const ensureFreshCatalog = useCallback(
    async (options = {}) => {
      const maxAgeMs = options.maxAgeMs ?? DEFAULT_STALE_MS;
      const minIntervalMs = options.minIntervalMs ?? DEFAULT_MIN_INTERVAL_MS;
      if (!storeId) return;
      // ברשת חלשה — לא מורידים מגוון מחדש; עובדים מהמטמון כדי שהניווט יישאר חלק
      if (shouldPreferLocalData() && !options.force) return;

      const ts = catalogTimestampRef.current;
      if (Date.now() - ts < maxAgeMs) return;

      const lock = catalogSyncLockRef.current;
      if (Date.now() - lock.lastAttempt < minIntervalMs) return;
      if (lock.inFlight) return lock.inFlight;

      lock.lastAttempt = Date.now();
      const run = async () => {
        setIsSyncing(true);
        try {
          await runLightPrefetch(storeId);
          await refreshLocal();
        } catch (_) {
          lock.lastAttempt = 0;
        } finally {
          setIsSyncing(false);
        }
      };
      const p = run();
      lock.inFlight = p.finally(() => {
        lock.inFlight = null;
      });
      return lock.inFlight;
    },
    [storeId, refreshLocal]
  );

  // כשחוזרים ללשונית – רענון רק ברשת טובה ומגוון ישן מעל ~30 דקות
  useEffect(() => {
    if (!storeId) return;
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      if (shouldPreferLocalData()) return;
      const ts = catalogTimestampRef.current;
      if (Date.now() - ts < 30 * 60_000) return;
      ensureFreshCatalog({ maxAgeMs: 30 * 60_000, minIntervalMs: 10 * 60_000 });
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [storeId, ensureFreshCatalog]);

  // כשהנתונים נטענו – אם עברה שעה מאז השמירה, עדכון מגוון ברקע (ללא תמונות) — רק ברשת טובה
  useEffect(() => {
    if (!storeId || !localData?.timestamp) return;
    if (shouldPreferLocalData()) return;

    const savedBucket = getHourBucket(localData.timestamp);
    const currentBucket = getHourBucket(Date.now());
    if (currentBucket <= savedBucket) return;
    if (lastRefreshedHourBucketRef.current === currentBucket) return;

    lastRefreshedHourBucketRef.current = currentBucket;
    setIsSyncing(true);
    runLightPrefetch(storeId)
      .then(() => refreshLocal())
      .catch(() => {
        lastRefreshedHourBucketRef.current = null;
      })
      .finally(() => setIsSyncing(false));
  }, [storeId, localData?.timestamp, refreshLocal]);

  /** מטמון lite בלי מכירות — ברשת חלשה מדלגים (בקשה כבדה שחונקת ניווט) */
  useEffect(() => {
    if (!storeId || !localData?.products?.length) return;
    if (shouldPreferLocalData()) return;
    const periodOk = localData.salesStatsPeriodVersion === SALES_STATS_PERIOD_VERSION;
    if ((localData.salesStatsPrefetched && periodOk) || salesStatsBackfillRef.current) return;
    salesStatsBackfillRef.current = true;

    fetchStoreSalesByItemCode(storeId)
      .then((byItemCode) =>
        savePrefetched(storeId, {
          products: attachSalesStatsToProducts(localData.products, byItemCode),
          categories: localData.categories,
          subcategories: localData.subcategories,
          subsubcategories: localData.subsubcategories,
          store: localData.store,
          companies: localData.companies,
          productsByCompany: localData.productsByCompany
            ? Object.fromEntries(
                Object.entries(localData.productsByCompany).map(([k, arr]) => [
                  k,
                  attachSalesStatsToProducts(arr, byItemCode),
                ])
              )
            : localData.productsByCompany,
          chain: localData.chain,
          lastPurchaseByProductId: localData.lastPurchaseByProductId,
          lastReceiptQtyByProductId: localData.lastReceiptQtyByProductId,
          lastReceiptQtyVersion: localData.lastReceiptQtyVersion,
          lastPurchasesPrefetched: localData.lastPurchasesPrefetched,
          salesStatsPrefetched: true,
          salesStatsPeriodVersion: SALES_STATS_PERIOD_VERSION,
          homeSnapshot: localData.homeSnapshot,
        })
      )
      .then(() => refreshLocal())
      .catch(() => {
        salesStatsBackfillRef.current = false;
      });
  }, [storeId, localData, refreshLocal]);

  /** היסטוריית רכישות — רק ברשת טובה; ברשת חלשה לא גונבים bandwidth מהניווט */
  useEffect(() => {
    if (!storeId || !localData?.products?.length || localData.lastPurchasesPrefetched) return;
    if (shouldPreferLocalData()) return;
    if (lastPurchaseBackfillRef.current) return;
    lastPurchaseBackfillRef.current = true;

    base44.entities.Order.getLastPurchasesForStore({ storeId })
      .then((res) =>
        savePrefetched(storeId, {
          products: localData.products,
          categories: localData.categories,
          subcategories: localData.subcategories,
          subsubcategories: localData.subsubcategories,
          store: localData.store,
          companies: localData.companies,
          productsByCompany: localData.productsByCompany,
          chain: localData.chain,
          lastPurchaseByProductId: res?.byProductId || {},
          lastReceiptQtyByProductId: res?.byProductQty || {},
          lastReceiptQtyVersion: LAST_RECEIPT_QTY_VERSION,
          lastPurchasesPrefetched: true,
          salesStatsPrefetched: localData.salesStatsPrefetched,
          salesStatsPeriodVersion: localData.salesStatsPeriodVersion,
          homeSnapshot: localData.homeSnapshot,
        })
      )
      .then(() => refreshLocal())
      .catch(() => {
        lastPurchaseBackfillRef.current = false;
      });
  }, [storeId, localData, refreshLocal]);

  /** צילום דשבורד לבית — ברשת טובה ממלאים אם חסר (מטמון ישן מלפני השדרוג) */
  useEffect(() => {
    if (!storeId || !localData?.products?.length) return;
    if (shouldPreferLocalData()) return;
    if (localData.homeSnapshot?.fetchedAt) return;
    if (homeSnapshotBackfillRef.current) return;
    homeSnapshotBackfillRef.current = true;

    fetchHomeSnapshot(storeId)
      .then((homeSnapshot) => {
        if (!homeSnapshot) {
          homeSnapshotBackfillRef.current = false;
          return null;
        }
        return savePrefetched(storeId, {
          products: localData.products,
          categories: localData.categories,
          subcategories: localData.subcategories,
          subsubcategories: localData.subsubcategories,
          store: localData.store,
          companies: localData.companies,
          productsByCompany: localData.productsByCompany,
          chain: localData.chain,
          lastPurchaseByProductId: localData.lastPurchaseByProductId,
          lastReceiptQtyByProductId: localData.lastReceiptQtyByProductId,
          lastReceiptQtyVersion: localData.lastReceiptQtyVersion,
          lastPurchasesPrefetched: localData.lastPurchasesPrefetched,
          salesStatsPrefetched: localData.salesStatsPrefetched,
          salesStatsPeriodVersion: localData.salesStatsPeriodVersion,
          homeSnapshot,
        }).then(() => refreshLocal());
      })
      .catch(() => {
        homeSnapshotBackfillRef.current = false;
      });
  }, [storeId, localData, refreshLocal]);

  /** אין מטמון (או גרסה ישנה) — מוריד מגוון מעודכן ברקע פעם אחת */
  useEffect(() => {
    if (!storeId || isPrefetchLoading) return;
    if (localData?.products?.length) return;
    if (autoCatalogPrefetchRef.current) return;
    autoCatalogPrefetchRef.current = true;

    setIsSyncing(true);
    runLightPrefetch(storeId)
      .then(() => refreshLocal())
      .catch(() => {
        autoCatalogPrefetchRef.current = false;
      })
      .finally(() => setIsSyncing(false));
  }, [storeId, isPrefetchLoading, localData?.products?.length, refreshLocal]);

  const value = {
    storeId,
    localData,
    refreshLocal,
    ensureFreshCatalog,
    isPrefetchLoading,
    lastSyncAt: localData?.timestamp ?? null,
    isSyncing,
    hasLocalData: !!(localData?.products?.length),
  };

  return (
    <LocalStoreContext.Provider value={value}>
      {children}
    </LocalStoreContext.Provider>
  );
}

export function useLocalStore() {
  const ctx = useContext(LocalStoreContext);
  if (!ctx) {
    throw new Error("useLocalStore must be used within LocalStoreProvider");
  }
  return ctx;
}
